import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
// import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import MicIcon from '@mui/icons-material/Mic';

import { fetch_one, fetch_all } from "../../../lib/v31lib";
import { loginAtom } from '../../../lib/auth';
import { currentPracticeAtom } from '../../../lib/practice';
import HelpersPractice from '../../../actions/helpers/practice';

import {
  Box,
  Button as Btn,
  Chip,
  CircularProgress,
  DialogActions,
  Divider,
  Grid,
  MenuItem,
  Tooltip,
} from '@mui/material';

import {
  Button,
  DatePicker,
  Dt,
  Editor,
  FileDrop,
  Select,
  TextInput,
  Typography
} from '../../../components/v2/styled';
import { Base } from './Base';
import { ChippaScribeDialog } from './ChippaScribe';
import { CoachEditAdviceTab0 } from './dialogFolders/CoachEditAdviceTab0';
import { CoachEditAdviceTab1 } from './dialogFolders/CoachEditAdviceTab1';
import { CoachEditAdviceTab2 } from './dialogFolders/CoachEditAdviceTab2';
import { CoachEditAdviceTab3 } from './dialogFolders/CoachEditAdviceTab3';
import { CoachEditAdviceTab4 } from './dialogFolders/CoachEditAdviceTab4';
import { CoachEditAdviceTab5 } from './dialogFolders/CoachEditAdviceTab5';

import { Advice } from '../../../models/Advice';
import { User } from '../../../models/User';

const { DateTime } = require("luxon");

type Props = {
  onClick: (b:boolean) => void;
  open: boolean;
  setOpen: (b:boolean) => void;
  advice: Advice;
  clonedAdvice: Advice;
  setAdvice: (d:Advice) => void;
  loading?: boolean;
  objects: Advice[];
  user: User;
};

export const CoachEditAdvice: FunctionComponent<Props> = ({objects, loading, open, setOpen, advice, clonedAdvice, setAdvice, onClick, user}) => {
  const theme = useTheme();
  // const navigate = useNavigate();
  const [login, setLogin] = useAtom(loginAtom);
  const [chippaOpen, setChippaOpen] = useState<boolean>(false);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const {t, i18n} = useTranslation(['translations']);
  const queryClient = useQueryClient();

  const canChippa = HelpersPractice.practice_modules_v3({practice: currentPractice, check: 'chippa'});

  const chippaImportMutation = useMutation({
    mutationFn: () => {
      return fetch_one<{success: boolean; status?: string}>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/chippa/import`,
        {advice_id: advice.id},
        login
      );
    },
    onSuccess: (data) => {
      if (!!data?.success) {
        queryClient.invalidateQueries({queryKey: ["advices"]});
        onClick(true);
      }
    }
  });

  const [flickerEditor, setFlickerEditor] = useState<boolean>(false);
  const [currentTab, setCurrentTab] = useState<number>(1);
  const [activeCompare, setActiveCompare] = useState<Advice>();
  const [progresses, setProgresses] = useState<{basic: number, internal: number, measure: number, food: number, advice: number}>({basic: 0, internal: 0, measure: 0, food: 0, advice: 0});

  useEffect(() => {
    setCurrentTab(1);
  }, []);

  useEffect(() => {
    let pr = {
      basic: 0, internal: 0, measure: 0, food: 0, advice: 0
    }
    let pr_sum = {
      basic: 3, internal: 3, measure: 2, food: 23, advice: 1
    }

    if (!!advice.comment) pr.internal += 1;
    if (!!advice.next_time) pr.internal += 1;
    if (!!advice.internal_comment) pr.internal += 1;

    if (!!advice.deals) pr.basic += 1;
    if (!!advice.goals) pr.basic += 1;
    if (!!advice.education) pr.basic += 1;

    if (!!advice.weight) pr.measure += 1;
    if (!!advice.waist) pr.measure += 1;

    if (!!advice?.anamnese?.breakfast) pr.food += 1;
    if (!!advice?.anamnese?.lunch) pr.food += 1;
    if (!!advice?.anamnese?.diner) pr.food += 1;
    if (!!advice?.anamnese?.snack_1) pr.food += 1;
    if (!!advice?.anamnese?.snack_2) pr.food += 1;
    if (!!advice?.anamnese?.snack_3) pr.food += 1;
    if (!!advice?.anamnese?.aversion) pr.food += 1;
    if (!!advice?.anamnese?.preference) pr.food += 1;
    if (!!advice?.anamnese?.saturday) pr.food += 1;
    if (!!advice?.anamnese?.sunday) pr.food += 1;
    if (!!advice?.anamnese?.weekend_general) pr.food += 1;
    if (!!advice?.anamnese?.pattern) pr.food += 1;
    if (!!advice?.anamnese?.fluids) pr.food += 1;
    if (!!advice?.anamnese?.extra_information) pr.food += 1;
    if (!!advice?.anamnese?.daily_dairy) pr.food += 1;
    if (!!advice?.anamnese?.daily_fats) pr.food += 1;
    if (!!advice?.anamnese?.daily_fruits) pr.food += 1;
    if (!!advice?.anamnese?.daily_grains) pr.food += 1;
    if (!!advice?.anamnese?.daily_meats) pr.food += 1;
    if (!!advice?.anamnese?.daily_proteins) pr.food += 1;
    if (!!advice?.anamnese?.daily_salts) pr.food += 1;
    if (!!advice?.anamnese?.daily_supplements) pr.food += 1;
    if (!!advice?.anamnese?.daily_veggies) pr.food += 1;

    if (!!advice.general_advice) pr.advice += 1;

    setProgresses({
      basic: pr.basic/pr_sum.basic*100,
      internal: pr.internal/pr_sum.internal*100,
      measure: pr.measure/pr_sum.measure*100,
      food: pr.food/pr_sum.food*100,
      advice: pr.advice/pr_sum.advice*100
    })
  }, [advice]);

  // const {
  //   data: coaches,
  //   isLoading: coachesLoading,
  //   isError: coachesError,
  //   isSuccess: coachesSuccess,
  //   // refetch: coachesRefetch,
  // } = useQuery({
  //   queryKey: ["coaches", currentPractice?.id],
  //   queryFn: () =>
  //     fetch_all<User>(
  //       `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
  //       {
  //         object: 'coach',
  //         fields: ['id', 'full_name'],
  //         filter: {
  //           search: '',
  //           advanced: {
  //             practice_id: (login?.practice_id || currentPractice?.id),
  //             is_secretary: '0',
  //             enabled: '1'
  //           }
  //         }
  //       },
  //       login
  //     ),
  //   enabled: !!currentPractice?.id
  // });








  return (<>
    <Base
      name="advice_edit"
      title={`${t("coach.submenu.advices.edit_advice")}: ${advice.identification} (${!!advice.advice_date ? DateTime.fromFormat(advice.advice_date, "dd/LL/y").toFormat("dd/LL/y") : '-'})`}
      fullWidth
      maxWidth="xl"
      dc_sx={{
        paddingBottom: 0
      }}
      content={<>
        {!!login?.settings?.nutri_advice?.use_advanced_index && <>
          <Box sx={{
            width: '100%',
            height: 'auto'
          }}>
            <Box sx={{
              // ...(!!activeCompare ? {
              //   width: "calc(50% - 100px)",
              //   maxWidth: "calc(50% - 100px)",
              // } : {
                width: "calc(100% - 180px)",
                maxWidth: "calc(100% - 180px)",
              // }),
              display: 'inline-block',
              float: 'left',
              height: 'auto',
              padding: 1,
              borderRadius: 1
            }}>
              {currentTab === 0 && <Grid container spacing={1}>
                <CoachEditAdviceTab0
                  open={open}
                  setOpen={setOpen}
                  advice={advice}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                />
              </Grid>}
              {currentTab === 1 && <Grid container spacing={1}>
                <CoachEditAdviceTab1
                  open={open}
                  setOpen={setOpen}
                  advice={advice}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                />
              </Grid>}
              {currentTab === 2 && <Grid container spacing={1}>
                <CoachEditAdviceTab2
                  open={open}
                  setOpen={setOpen}
                  advice={advice}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                  objects={objects}
                  weightTrigger={!!user.extra_data?.weight_trigger}
                />
              </Grid>}
              {currentTab === 3 && <Grid container spacing={1} sx={{
                "& p": {
                  marginTop: 0,
                  marginBottom: 0
                }
              }}>
                <CoachEditAdviceTab3
                  open={open}
                  setOpen={setOpen}
                  advice={advice}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                />
              </Grid>}
              {currentTab === 4 && <Grid container spacing={1} sx={{
                "& p": {
                  marginTop: 0,
                  marginBottom: 0
                }
              }}>
                <CoachEditAdviceTab4
                  open={open}
                  setOpen={setOpen}
                  advice={advice}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                />
              </Grid>}
              {currentTab === 5 && <Grid container spacing={1}>
                <CoachEditAdviceTab5
                  open={open}
                  setOpen={setOpen}
                  advice={advice}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                  userId={user?.id}
                />
              </Grid>}
              {currentTab === 6 && <Grid container spacing={1} sx={{minHeight: '450px'}}>
                <Typography html sx={{whiteSpace: 'pre-wrap'}}>{advice.chippa_report}</Typography>
              </Grid>}
            </Box>
            {/* {!!activeCompare && <Box sx={{
              width: "calc(50% - 100px)",
              maxWidth: "calc(50% - 100px)",
              display: 'inline-block',
              float: 'left',
              height: 'auto',
              padding: 1,
              borderRadius: 1
            }}>
              {currentTab === 0 && <Grid container spacing={1}>
                <CoachEditAdviceTab0
                  open={open}
                  setOpen={setOpen}
                  advice={activeCompare}
                  setAdvice={setActiveCompare}
                  loading={loading}
                  onClick={onClick}
                  isCompare
                />
              </Grid>}
              {currentTab === 1 && <Grid container spacing={1}>
                <CoachEditAdviceTab1
                  open={open}
                  setOpen={setOpen}
                  advice={activeCompare}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                />
              </Grid>}
              {currentTab === 2 && <Grid container spacing={1}>
                <CoachEditAdviceTab2
                  open={open}
                  setOpen={setOpen}
                  advice={activeCompare}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                  weightTrigger={!!user.extra_data?.weight_trigger}
                />
              </Grid>}
              {currentTab === 3 && <Grid container spacing={1}>

              </Grid>}
              {currentTab === 4 && <Grid container spacing={1} sx={{
                "& p": {
                  marginTop: 0,
                  marginBottom: 0
                }
              }}>
                <CoachEditAdviceTab4
                  open={open}
                  setOpen={setOpen}
                  advice={activeCompare}
                  clonedAdvice={clonedAdvice}
                  setAdvice={setAdvice}
                  loading={loading}
                  onClick={onClick}
                  activeCompare={activeCompare}
                />
              </Grid>}
              {currentTab === 5 && <Grid container spacing={1}>

              </Grid>}
            </Box>} */}
            <Box sx={{
              width: '180px',
              maxWidth: '180px',
              display: 'inline-block',
              float: 'left',
              height: 'auto',
              padding: 1,
              borderRadius: 1,
              border: '1px solid transparent',
              borderColor: theme.palette.primary.main,
              position: 'fixed'
            }}>
              <Button
                sx={{
                  marginBottom: 1,
                  marginTop: 0,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  },
                }}
                id="menu_internal"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(1);
                }}
                label={t("new_advices.detail.menu_internal")}
                fullwidth
                contained={currentTab === 1}
                endIcon={<CircularProgress
                  variant="determinate"
                  sx={{
                    color: currentTab === 1 ? 'white' : theme.palette.primary.main,
                    height: '20px !important',
                    width: '20px !important'
                  }}
                  value={progresses.internal}
                />}
              />
              <Button
                sx={{
                  marginBottom: 1,
                  marginTop: 0,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  },
                }}
                id="menu_basic"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(0);
                }}
                label={t("new_advices.detail.menu_basic")}
                fullwidth
                contained={currentTab === 0}
                endIcon={<CircularProgress
                  variant="determinate"
                  sx={{
                    color: currentTab === 0 ? 'white' : theme.palette.primary.main,
                    height: '20px !important',
                    width: '20px !important'
                  }}
                  value={progresses.basic}
                />}
              />
              <Button
                sx={{
                  marginBottom: 1,
                  marginTop: 0,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  },
                }}
                id="menu_measure"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(2);
                }}
                label={t("new_advices.detail.menu_measure")}
                fullwidth
                contained={currentTab === 2}
                endIcon={<CircularProgress
                  variant="determinate"
                  sx={{
                    color: currentTab === 2 ? 'white' : theme.palette.primary.main,
                    height: '20px !important',
                    width: '20px !important'
                  }}
                  value={progresses.measure}
                />}
              />
              <Button
                sx={{
                  marginBottom: 1,
                  marginTop: 0,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  },
                }}
                id="menu_medication"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(3);
                }}
                label={t("new_advices.detail.menu_medication")}
                fullwidth
                contained={currentTab === 3}
                endIcon={<CircularProgress
                  variant="determinate"
                  sx={{
                    color: currentTab === 3 ? 'white' : theme.palette.primary.main,
                    height: '20px !important',
                    width: '20px !important'
                  }}
                  value={0}
                />}
              />
              <Button
                sx={{
                  marginBottom: 1,
                  marginTop: 0,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  },
                }}
                id="menu_food"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(4);
                }}
                label={t("new_advices.detail.menu_food")}
                fullwidth
                contained={currentTab === 4}
                endIcon={<CircularProgress
                  variant="determinate"
                  sx={{
                    color: currentTab === 4 ? 'white' : theme.palette.primary.main,
                    height: '20px !important',
                    width: '20px !important'
                  }}
                  value={progresses.food}
                />}
              />
              <Button
                sx={{
                  marginBottom: 0,
                  marginTop: 0,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  },
                }}
                id="menu_day"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(5);
                }}
                label={t("new_advices.detail.menu_day")}
                fullwidth
                contained={currentTab === 5}
                endIcon={<CircularProgress
                  variant="determinate"
                  sx={{
                    color: currentTab === 5 ? 'white' : theme.palette.primary.main,
                    height: '20px !important',
                    width: '20px !important'
                  }}
                  value={progresses.advice}
                />} 
              />
              {!!advice.chippa_report && <Button
                sx={{
                  marginBottom: 0,
                  marginTop: 1,
                  "& .MuiButton-endIcon": {
                    position: "absolute",
                    right: "1rem",
                  },
                  "& .MuiButton-startIcon": {
                    position: "absolute",
                    left: "1rem",
                  }
                }}
                id="tab_chippa"
                onClick={(e) => {
                  e.preventDefault();
                  setCurrentTab(6);
                }}
                label={t("new_advices.detail.menu_chippa", "Scribe")}
                fullwidth
                contained={currentTab === 6}
              />}
              <Divider sx={{marginTop: 1, marginBottom: 1}} />
              <Typography>{t("new_advices.detail.compare.title", "Vergelijk")}</Typography>
              <Select
                fullWidth
                displayEmpty
                caption={t("new_advices.detail.compare.previous_consults")}
                id="previous_consults"
                value={activeCompare?.id?.toString() || ''}
                setValue={(v) => {
                  setActiveCompare((objects.filter(fff => (fff.id || '0').toString() === v?.toString()) || [])[0]);
                }}
              >
                <MenuItem value="">------</MenuItem>
                {[...objects].sort((a, b) => ((b.epi || 0) * 10000 + (b.idx || 0)) - ((a.epi || 0) * 10000 + (a.idx || 0))).filter(fff => (fff.id || '') !== advice?.id).map(adv => <MenuItem value={adv.id}>{adv.identification} (<Dt d={adv.advice_date} />)</MenuItem>)}
              </Select>
              {!!activeCompare && <>
                <Typography sx={{
                  marginTop: 1,
                  textDecoration: 'underline',
                  color: theme.palette.primary.main,
                  cursor: 'pointer'
                }} onClick={() => {
                  setAdvice({
                    ...activeCompare,
                    advice_date: DateTime.fromISO(activeCompare.advice_date).toFormat("dd/LL/y")
                  });
                  setActiveCompare(undefined);
                }}>{t("new_advices.detail.compare.open_comparison")}</Typography>
              </>}
            </Box>
            <Box sx={{
              clear: 'both'
            }} />
          </Box>
        </>}






        {!login?.settings?.nutri_advice?.use_advanced_index && <Grid container spacing={1}>
          {!advice.id && <Grid item xs={12}>
            <Typography sx={{
              padding: 1,
              border: '1px solid transparent',
              borderColor: theme.palette.warning.main
            }}>{t(!advice.cloned ? "coach.submenu.advices.help_set_cloned" : "coach.submenu.advices.help_set_empty")} <Button
              sx={{fontSize: '0.7rem', marginLeft: 1, marginTop: 0}}
              id="switch"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                setFlickerEditor(true);

                if (!!advice.cloned) {
                  setAdvice({
                    attachments: [],
                    advice_date: DateTime.now().toFormat("dd/LL/y")
                  });
                } else {
                  setAdvice({
                    ...clonedAdvice,
                    advice_date: DateTime.now().toFormat("dd/LL/y")
                  });
                }

                setTimeout(() => {setFlickerEditor(false);}, 200);
              }}
              label={t(!advice.cloned ? "coach.submenu.advices.cta_set_cloned" : "coach.submenu.advices.cta_set_empty")}
            /></Typography>
          </Grid>}

          <Grid item xs={12} md={1}>
            <TextInput
              id="idx"
              caption={t("client.models.advices.idx", "Index")}
              placeholder={t("client.models.advices.placeholders.idx", "1")}
              value={advice?.idx?.toString() || ""}
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  idx: !!e ? parseInt(e, 10) : 1
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="caption">{t("coach.models.advices.advice_date", "Datum")}</Typography><br/>
            <DatePicker
              disableFuture={false}
              openTo="day"
              label=""
              format={"dd/LL/y"}
              views={["year", "month", "day"]}
              setValue={(e) => {
                setAdvice({
                  ...advice,
                  advice_date: e
                });
              }}
              value={advice.advice_date || ''}
              id="option_date"
              sx={{
                box: {
                  width: 'auto',
                  display: 'inline-block'
                },
                field_box: {
                  "svg": {
                    display: 'none'
                  }
                },
                field: {
                  width: 'auto',
                  marginTop: 0,
                  "input": {
                    textAlign: 'center'
                  }
                }
              }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextInput
              id="weight"
              caption={t("client.models.advices.weight", "Gewicht (kg)")}
              placeholder={t("client.models.advices.placeholders.weight", "00.0")}
              value={advice?.weight || ""}
              outside_world
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  weight: (e || '').replace(/,/g, '.')
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextInput
              id="waist"
              caption={t("client.models.advices.waist", "Middelomtrek (cm)")}
              placeholder={t("client.models.advices.placeholders.waist", "000")}
              value={advice?.waist || ""}
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  waist: e
                });
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            {!flickerEditor && <Editor
              advanced
              label={t("coach.models.advices.comment", "Commentaar")}
              value={advice?.comment || ""}
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  comment: e
                });
              }}
            />}
          </Grid>
          <Grid item xs={12} md={6}>
            {!flickerEditor && <Editor
              advanced
              label={t("coach.models.advices.deals", "Afspraken")}
              value={advice?.deals || ""}
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  deals: e
                });
              }}
            />}
          </Grid>
          {!!advice.feedback_deals && <>
            <Grid item xs={12} md={6}></Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{fontSize: '0.7rem'}}>{t("coach.models.advices.feedback_deals", "Opmerking van de client (afspraken)")}</Typography>
              <Typography html sx={{marginTop: '-1em', paddingLeft: 2, borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{advice.feedback_deals}</Typography>
            </Grid>
          </>}

          <Grid item xs={12} md={6}>
            {!flickerEditor && <Editor
              advanced
              label={t("coach.models.advices.goals", "Doelstelling")}
              value={advice?.goals || ""}
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  goals: e
                });
              }}
            />}
          </Grid>
          <Grid item xs={12} md={6}>
            {!flickerEditor && <Editor
              advanced
              label={t("coach.models.advices.education", "Educatie")}
              value={advice?.education || ""}
              onChange={(e) => {
                setAdvice({
                  ...advice,
                  education: e
                });
              }}
            />}
          </Grid>
          {(!!advice.feedback_goals || !!advice.feedback_education) && <>
            <Grid item xs={12} md={6}>
              {!!advice.feedback_goals && <Typography sx={{fontSize: '0.7rem'}}>{t("coach.models.advices.feedback_goals", "Opmerking van de client (goals)")}</Typography>}
              {!!advice.feedback_goals && <Typography html sx={{marginTop: '-1em', paddingLeft: 2, borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{advice.feedback_goals}</Typography>}
            </Grid>
            <Grid item xs={12} md={6}>
              {!!advice.feedback_education && <Typography sx={{fontSize: '0.7rem'}}>{t("coach.models.advices.feedback_education", "Opmerking van de client (educatie)")}</Typography>}
              {!!advice.feedback_education && <Typography html sx={{marginTop: '-1em', paddingLeft: 2, borderLeft: '2px solid transparent', borderColor: theme.palette.primary.main}}>{advice.feedback_education}</Typography>}
            </Grid>
          </>}

          <Grid item xs={6}>
            {/* <Dropzone
              filesLimit={5}
              maxFileSize={26214400}
              showFileNamesInPreview={true}
              showFileNames={true}
              initialFiles={this.state.active_advice.attachments ? this.state.active_advice.attachments.map((f) => f.attributes.url) : []}
              onChange={(files) => {
                this.setState({
                  files: files
                });
              }}
            /> */}
            <Typography sx={{fontSize: '0.7rem'}}>{t("coach.models.advices.attachments", "Bijlagen")}</Typography>
            <FileDrop
              onDrop={acceptedFiles => {setAdvice({
                ...advice,
                items: acceptedFiles
              });}}
              multiple={true}
              maxSize={20 * 1000 * 1000}
              items={advice.items}
              dropText="Click/Drop"
              dropHint="All.: all files, < 10mb"
            />
          </Grid>
          <Grid item xs={6}>
            {/* <Dropzone
              filesLimit={5}
              maxFileSize={26214400}
              showFileNamesInPreview={true}
              showFileNames={true}
              initialFiles={this.state.active_advice.attachments ? this.state.active_advice.attachments.map((f) => f.attributes.url) : []}
              onChange={(files) => {
                this.setState({
                  files: files
                });
              }}
            /> */}
            <Typography sx={{fontSize: '0.7rem'}}>{t("coach.models.advices.attachments_private", "Bijlagen, niet zichtbaar voor de client")}</Typography>
            <FileDrop
              onDrop={acceptedFiles => {setAdvice({
                ...advice,
                items_private: acceptedFiles
              });}}
              multiple={true}
              maxSize={20 * 1000 * 1000}
              items={advice.items_private}
              dropText="Click/Drop"
              dropHint="All.: all files, < 10mb"
            />
          </Grid>
          <Grid item xs={12}>
            {(advice.attachments || []).map(item => <Tooltip title={<Box sx={{maxWidth: 300, maxHeight: 300}}><img src={item.url} style={{maxWidth: 280, maxHeight: 280, width: 'auto', height: 'auto'}} /></Box>}><Chip label={item.file_name} variant="outlined" color="primary" sx={{marginRight: 0.5}} onDelete={() => {setAdvice({...advice, attachments: advice.attachments.filter(fff => fff.id !== item.id)});}} /></Tooltip>)}
          </Grid>

          {!!advice.id && <Grid item xs={12}>
            <Box sx={{
              border: '1px solid transparent',
              borderColor: theme.palette.primary.main,
              padding: 1,
              borderRadius: 1,
              marginTop: 1
            }}>
              {!!advice && <Typography>{t("coach.submenu.advices.time_created")}: <Dt d={advice.created_at} f="cccc DDD t" />{!!advice.creator_id && ` ${t("general.terms.by")} ${advice.creator_full_name}`}</Typography>}
              {!!advice && <Typography>{t("coach.submenu.advices.time_updated")}: <Dt d={advice.updated_at} f="cccc DDD t" />{!!advice.editor_id && ` ${t("general.terms.by")} ${advice.editor_full_name}`}</Typography>}
            </Box>
          </Grid>}

        </Grid>}
      </>}
      actions={<DialogActions sx={{borderTop: '1px solid transparent', borderColor: theme.palette.primary.main}}>
        {!!loading && <CircularProgress />}
        {canChippa && <Btn
          onClick={() => {setChippaOpen(true);}}
          disabled={!advice.id}
          startIcon={<MicIcon />}
          color="primary"
        >{t("coach.chippa.open")}</Btn>}
        {/* {!advice.chippa_report && <Btn
          onClick={() => {chippaImportMutation.mutate();}}
          disabled={!advice.id || chippaImportMutation.isPending}
          color="primary"
        >{chippaImportMutation.isPending ? t("coach.chippa.checking") : t("coach.chippa.fetch_report", "Verslag binnentrekken")}</Btn>} */}
        <Btn
          onClick={(e) => {
            setCurrentTab(1);
            setOpen(false);
          }}
          disabled={currentTab === 3 || loading}
          color="primary"
        >{t("coach.general.actions.close")}</Btn>
        {currentTab !== 3 && <Btn
          onClick={() => {
            onClick(false);
            setCurrentTab(1);
          }}
          disabled={loading}
          color="primary"
        >{t("coach.general.actions.save")}</Btn>}
        {currentTab !== 3 && <Btn
          onClick={() => {
            onClick(true);
            // setCurrentTab(1);
          }}
          disabled={loading}
          color="primary"
        >{t("coach.general.actions.save_and_keep_open")}</Btn>}
      </DialogActions>}
      open={open}
      setOpen={setOpen}
      onClose={() => {
        setCurrentTab(1);
      }}
    />
    <ChippaScribeDialog
      open={chippaOpen}
      setOpen={setChippaOpen}
      advice={advice}
      user={user}
    />
  </>);
}
